import { MessageReactionName } from "@redotech/redo-agents-cards/support-agents/message-reaction";
import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import { emailTemplateSchema } from "@redotech/redo-marketing-templates-common/email-template";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum MessageDirection {
  INBOUND = "INBOUND",
  OUTBOUND = "OUTBOUND",
}

/** Minimal conversation turn a caller can pass into recover reply classification. */
export interface RecoverConversationTurn {
  readonly direction: MessageDirection;
  readonly content: string;
}

export enum MarketingMessageType {
  INITIAL = "INITIAL",
  REPLY = "REPLY",
}

export const baseMarketingMessageSchema_ = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  conversationId: zExt.objectId(),
  direction: createEnum(MessageDirection),
  type: createEnum(MarketingMessageType),
  to: z.string(),
  from: z.string(),
  llmThreadId: zExt.objectId().nullish(),
  idempotencyKey: z.string().optional(), // Webhook unique ID for inbound messages only
  externalMessageId: z.string().nullish(), // Provider's native message id (e.g. Apple GUID) for inbound; targets tapback reactions
  reaction: createEnum(MessageReactionName).nullish(), // Agent tapback reaction stamped on this inbound message

  draftedAt: z.date().nullish(),
  scheduledAt: z.date().nullish(),
  sentAt: z.date().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
  failedAt: z.date().nullish(),
  /**
   * Support V2 `draft_message` PK (pre-send draft) linked to this marketing draft.
   * Stored as the raw CRDB INT8 id in decimal-string form (not the effect-api
   * `dmsg_…` prefixed form) so non-effect-api consumers can join it directly.
   */
  supportDraftId: z.string().nullish(),
  /**
   * Support V2 `message` PK (the sent message) linked to this marketing message.
   * Stored as the raw CRDB INT8 id in decimal-string form (not the `msg_…`
   * prefixed form).
   */
  supportMessageId: z.string().nullish(),
});
export const baseMarketingMessageSchema: z.ZodType<BaseMarketingMessage> =
  baseMarketingMessageSchema_;
export interface BaseMarketingMessage extends z.infer<
  typeof baseMarketingMessageSchema_
> {}

const marketingMessageAttachmentSchema = z.object({
  url: z.string(),
  contentType: z.string().optional(),
});

const smsMarketingMessageSchema_ = baseMarketingMessageSchema_.extend({
  channel: z.literal(MarketingChannel.SMS),
  content: z.string(),
  attachments: z.array(marketingMessageAttachmentSchema).optional(),
});
export const smsMarketingMessageSchema: z.ZodType<SmsMarketingMessage> =
  smsMarketingMessageSchema_;
export interface SmsMarketingMessage extends z.infer<
  typeof smsMarketingMessageSchema_
> {}

const emailMarketingMessageSchema_ = baseMarketingMessageSchema_.extend({
  channel: z.literal(MarketingChannel.EMAIL),
  emailTemplate: emailTemplateSchema,
});
export const emailMarketingMessageSchema: z.ZodType<EmailMarketingMessage> =
  emailMarketingMessageSchema_;
export interface EmailMarketingMessage extends z.infer<
  typeof emailMarketingMessageSchema_
> {}

const marketingMessageSchema_ = z.discriminatedUnion("channel", [
  smsMarketingMessageSchema_,
  emailMarketingMessageSchema_,
]);
export const marketingMessageSchema: z.ZodType<MarketingMessage> =
  marketingMessageSchema_;
export type MarketingMessage = SmsMarketingMessage | EmailMarketingMessage;
